import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Banner, Breadcrumbs, Button, Loader, Tabs } from '@cloudflare/kumo'
import { HouseIcon, BuildingsIcon, PlusIcon } from '@phosphor-icons/react'
import { apiRequest, ApiError } from '../api/client'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import { DayNavigator } from '../components/day-navigator/day-navigator'
import {
  addDaysInTz,
  addMonthsInTz,
  addWeeksInTz,
  rangeForViewMode,
  todayYmdInTz,
} from '../lib/calendar-time'
import { BuildingCalendarDayView } from './BuildingCalendarDayView'
import { BuildingCalendarWeekView } from './BuildingCalendarWeekView'
import { BuildingCalendarMonthView } from './BuildingCalendarMonthView'
import type { Building, BuildingCalendarResponse } from '@hub-wifi/shared'

type ViewMode = 'day' | 'week' | 'month'

const YMD_REGEX = /^\d{4}-\d{2}-\d{2}$/

/** Narrow the URL :viewMode param to a known ViewMode, defaulting to 'day'. */
function toViewMode(raw: string | undefined): ViewMode {
  if (raw === 'week' || raw === 'month' || raw === 'day') return raw
  return 'day'
}

/**
 * Translate a calendar API error to a user-facing string. Mirrors
 * BuildingBookingsPage.describeBookingError pattern.
 */
function describeCalendarError(err: unknown, t: (key: string) => string): string {
  if (err instanceof ApiError) {
    const body = (err.body ?? {}) as { code?: string; message?: string }
    const code = body.code ?? err.message
    const translated = t(`buildingCalendar.errors.${code}`)
    if (translated && translated !== `buildingCalendar.errors.${code}`) return translated
    const fallback = t(`bookings.errors.${code}`)
    if (fallback && fallback !== `bookings.errors.${code}`) return fallback
    if (body.message) return body.message
    return code || t('common.error')
  }
  return t('common.error')
}

export function BuildingCalendarPage() {
  const { id, viewMode: rawViewMode } = useParams<{ id: string; viewMode: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()

  const viewMode = toViewMode(rawViewMode)

  const [building, setBuilding] = useState<Building | null>(null)
  const [data, setData] = useState<BuildingCalendarResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Derive the selected date from ?date= when it's a valid YYYY-MM-DD; else
  // fall back to today in the building's tz (only once building is known).
  // While building is still loading and date param is absent, leave undefined
  // and skip the fetch effect.
  const rawDateParam = searchParams.get('date')
  const dateParamIsValid = rawDateParam !== null && YMD_REGEX.test(rawDateParam)
  const selectedDate: string | undefined = dateParamIsValid
    ? rawDateParam!
    : building
      ? todayYmdInTz(building.timezone)
      : undefined

  // Load the building once for breadcrumb + timezone.
  useEffect(() => {
    if (!id) return
    let cancelled = false
    async function init() {
      try {
        const token = await getAccessTokenSilently()
        const b = await apiRequest<Building>(`/api/buildings/${id}`, { token })
        if (cancelled) return
        setBuilding(b)
      } catch (err) {
        if (!cancelled) setError(describeCalendarError(err, t))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void init()
    return () => {
      cancelled = true
    }
  }, [id, getAccessTokenSilently, t])

  // Fetch whenever building is known + date or view mode changes.
  useEffect(() => {
    if (!building || !selectedDate) return
    let cancelled = false
    async function run() {
      const range = rangeForViewMode(viewMode, selectedDate!, building!.timezone)
      // Empty/malformed input (user cleared the date) — keep previous data,
      // don't fire a NaN query.
      if (!range) return
      setRefreshing(true)
      try {
        const token = await getAccessTokenSilently()
        const qs = new URLSearchParams({ from: range.from, to: range.to })
        const resp = await apiRequest<BuildingCalendarResponse>(
          `/api/buildings/${id}/calendar?${qs.toString()}`,
          { token },
        )
        if (cancelled) return
        setData(resp)
        setError(null)
      } catch (err) {
        if (!cancelled) setError(describeCalendarError(err, t))
      } finally {
        if (!cancelled) {
          setRefreshing(false)
          setLoading(false)
        }
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [building, selectedDate, viewMode, id, getAccessTokenSilently, t])

  /**
   * Update the ?date= search param, REPLACING the current history entry so the
   * browser back button exits the calendar instead of walking back through
   * every prev/next step.
   */
  const updateDate = (ymd: string) => {
    setSearchParams(
      (prev) => {
        prev.set('date', ymd)
        return prev
      },
      { replace: true },
    )
  }

  /**
   * Switch to a new view-mode route. PUSHES a new history entry so the back
   * button returns to the previous view-mode.
   */
  const goToViewMode = (next: ViewMode) => {
    if (next === viewMode) return
    const qs = selectedDate ? `?${new URLSearchParams({ date: selectedDate }).toString()}` : ''
    navigate(`/buildings/${id}/calendar/${next}${qs}`)
  }

  const handlePrev = () => {
    if (!building || !selectedDate) return
    if (viewMode === 'day') updateDate(addDaysInTz(selectedDate, -1, building.timezone))
    else if (viewMode === 'week') updateDate(addWeeksInTz(selectedDate, -1, building.timezone))
    else updateDate(addMonthsInTz(selectedDate, -1, building.timezone))
  }

  const handleNext = () => {
    if (!building || !selectedDate) return
    if (viewMode === 'day') updateDate(addDaysInTz(selectedDate, 1, building.timezone))
    else if (viewMode === 'week') updateDate(addWeeksInTz(selectedDate, 1, building.timezone))
    else updateDate(addMonthsInTz(selectedDate, 1, building.timezone))
  }

  if (!id) return null

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Link icon={<BuildingsIcon size={14} />} href="/buildings">
              {t('buildings.title')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>
              {building?.name ?? '...'} - {t('buildingCalendar.title')}
            </Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('buildingCalendar.title')}
        description={building?.name ?? undefined}
        tabs={[
          { label: t('bookings.tab.list'), value: 'list' },
          { label: t('bookings.tab.calendar'), value: 'calendar' },
        ]}
        defaultTab="calendar"
        onValueChange={(v) => {
          if (v === 'list') navigate(`/buildings/${id}/bookings`)
        }}
      >
        <Link to={`/buildings/${id}/bookings/new`}>
          <Button variant="primary" size="sm" icon={<PlusIcon size={16} />}>
            {t('bookings.newBooking')}
          </Button>
        </Link>
      </PageHeader>

      {error && (
        <Banner variant="error" className="mb-3">
          {t('common.errorPrefix')} {error}
        </Banner>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <DayNavigator
          value={selectedDate ?? ''}
          timezone={building?.timezone}
          onChange={updateDate}
          onPrev={handlePrev}
          onNext={handleNext}
          disabledPrev={!building}
          disabledNext={!building}
          prevLabel={t('buildingCalendar.prev')}
          nextLabel={t('buildingCalendar.next')}
          openLabel={t('buildingCalendar.openCalendar')}
          todayLabel={t('dayNavigator.today')}
          prevTestId="calendar-prev-button"
          nextTestId="calendar-next-button"
          triggerTestId="calendar-date-input"
        />
        <div data-testid="calendar-viewmode-tabs">
          <Tabs
            tabs={[
              { value: 'day', label: t('buildingCalendar.viewMode.day') },
              { value: 'week', label: t('buildingCalendar.viewMode.week') },
              { value: 'month', label: t('buildingCalendar.viewMode.month') },
            ]}
            value={viewMode}
            onValueChange={(v) => goToViewMode(v as ViewMode)}
          />
        </div>
        {refreshing && <Loader size="sm" />}
      </div>

      {loading && !data && (
        <div className="flex items-center justify-center py-16">
          <Loader size="lg" />
        </div>
      )}

      {!loading && data && selectedDate && viewMode === 'day' && (
        <BuildingCalendarDayView date={selectedDate} data={data} loading={refreshing} />
      )}
      {!loading && data && selectedDate && viewMode === 'week' && (
        <BuildingCalendarWeekView date={selectedDate} data={data} loading={refreshing} />
      )}
      {!loading && data && selectedDate && viewMode === 'month' && (
        <BuildingCalendarMonthView
          date={selectedDate}
          data={data}
          loading={refreshing}
          onDrillIntoDay={(ymd) =>
            navigate(
              `/buildings/${id}/calendar/day?${new URLSearchParams({ date: ymd }).toString()}`,
            )
          }
        />
      )}
    </div>
  )
}
